import { useIncomingDocument } from '@/contexts/DocumentWorkspace';
import { useState, useCallback } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  Upload,
  X,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Layers,
  Loader2,
} from 'lucide-react';
import { pdfjsLib } from '@/lib/pdfWorker';
import { readPdfLibBytes, PdfLockedError, pdfLockedFilesMessage } from '@/lib/pdfIntake';
import { mergePDFs, formatFileSize } from '@/lib/pdfOperations';
import PageCanvas from '@/components/PageCanvas';
import { useFileDialog } from '@/hooks/useFileDialog';
import CloudSourceHint from '@/components/CloudSourceHint';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../lib/webUsage';
import FreeTierBanner from '../components/FreeTierBanner';

let fileIdCounter = 0;

/**
 * Merge — Combine multiple PDFs into one.
 *
 * Upload multiple files, reorder via drag-and-drop with live preview,
 * expand to select/deselect individual pages, then merge with pdf-lib.
 * After merge, auto-opens in the Viewer with a download button.
 */
export default function Merge() {
  const navigate = useNavigate();
  const { openFiles } = useFileDialog();
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [merging, setMerging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  // --- Drag reorder state (in state, not refs, so cards reorder live) ---
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);

  // --- Add files ---
  const addFiles = useCallback(async (fileList) => {
    const pdfs = Array.from(fileList).filter(
      (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name)
    );
    if (pdfs.length === 0) { setError('Choose PDF files to merge.'); return; }

    const locked = [];
    const failures = [];
    const newEntries = (await Promise.all(
      pdfs.map(async (file) => {
        const id = `file-${++fileIdCounter}`;
        // mergePDFs (pdf-lib) refuses encrypted input, including the common
        // owner-password-only kind PDF.js renders fine. Decrypt each file
        // losslessly at intake; a truly locked one is reported, not queued.
        let arrayBuffer;
        try {
          arrayBuffer = await readPdfLibBytes(file);
        } catch (err) {
          if (err instanceof PdfLockedError) {
            locked.push(file.name);
            return null;
          }
          failures.push(`${file.name}: ${err.message}`);
          return null;
        }
        let doc;
        try { doc = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) }).promise; }
        catch (err) { failures.push(`${file.name}: ${err.message}`); return null; }
        const numPages = doc.numPages;
        const selectedPages = new Set(
          Array.from({ length: numPages }, (_, i) => i + 1)
        );
        return {
          id,
          file,
          name: file.name,
          size: file.size,
          pdfDoc: doc,
          numPages,
          arrayBuffer,
          selectedPages,
          expanded: false,
        };
      })
    )).filter(Boolean);

    setFiles((prev) => [...prev, ...newEntries]);
    setError(
      locked.length
        ? pdfLockedFilesMessage(locked, locked.length > 1 ? 'merge them' : 'merge it')
        : failures.length ? `Could not open ${failures.join('; ')}` : null
    );
  }, []);

  useIncomingDocument((file) => addFiles([file]));

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleBrowse = useCallback(async () => {
    const results = await openFiles('.pdf');
    if (results.length > 0) {
      const fileList = results.map(r => new File([r.buffer], r.name, { type: 'application/pdf' }));
      addFiles(fileList);
    }
  }, [openFiles, addFiles]);

  const handleCloudPicked = useCallback((results) => {
    const fileList = results.map((r) => new File([r.buffer], r.name, { type: 'application/pdf' }));
    addFiles(fileList);
  }, [addFiles]);

  // --- Remove file ---
  const removeFile = useCallback((id) => {
    setFiles((prev) => {
      const entry = prev.find((f) => f.id === id);
      if (entry?.pdfDoc) entry.pdfDoc.destroy();
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  // --- Toggle expand ---
  const toggleExpand = useCallback((id) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, expanded: !f.expanded } : f))
    );
  }, []);

  // --- Toggle page selection ---
  const togglePage = useCallback((fileId, pageNum) => {
    setFiles((prev) =>
      prev.map((f) => {
        if (f.id !== fileId) return f;
        const next = new Set(f.selectedPages);
        if (next.has(pageNum)) {
          next.delete(pageNum);
        } else {
          next.add(pageNum);
        }
        return { ...f, selectedPages: next };
      })
    );
  }, []);

  // --- Drag reorder (live visual feedback) ---
  const handleDragStart = useCallback((e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Set a minimal drag image so the browser's ghost doesn't obscure cards
    if (e.target.querySelector) {
      e.dataTransfer.setDragImage(e.currentTarget, 20, 20);
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    // Commit the reorder if we have a valid drop target
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      setFiles((prev) => {
        const next = [...prev];
        const [dragged] = next.splice(dragIndex, 1);
        next.splice(dragOverIndex, 0, dragged);
        return next;
      });
    }
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, dragOverIndex]);

  const handleDragOverCard = useCallback(
    (e, index) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (index !== dragOverIndex) {
        setDragOverIndex(index);
      }
    },
    [dragOverIndex]
  );

  // Compute the visual order during drag
  const displayFiles = (() => {
    if (dragIndex === null || dragOverIndex === null || dragIndex === dragOverIndex) {
      return files;
    }
    const reordered = [...files];
    const [dragged] = reordered.splice(dragIndex, 1);
    reordered.splice(dragOverIndex, 0, dragged);
    return reordered;
  })();

  // --- Merge & open in viewer ---
  const handleMerge = useCallback(async () => {
    const sources = files
      .filter((f) => f.selectedPages.size > 0)
      .map((f) => ({
        arrayBuffer: f.arrayBuffer,
        selectedPages: f.selectedPages,
      }));

    if (sources.length === 0) return;

    if (usageCapApplies() && hasReachedLimit()) {
      setShowFreeTierModal(true);
      return;
    }

    setMerging(true);
    setProgress(0);
    setError(null);

    try {
      const mergedBytes = await mergePDFs(sources, setProgress);
      const blob = new Blob([mergedBytes], { type: 'application/pdf' });

      // Default filename: first file's name + _merged
      const baseName = files[0].name.replace(/\.pdf$/i, '');
      const mergedName = `${baseName}_merged.pdf`;

      const file = new File([blob], mergedName, { type: 'application/pdf' });
      if (usageCapApplies()) incrementUsage();
      navigate('/viewer', { state: { file } });
    } catch (err) {
      setError(`Merge failed: ${err.message}`);
      setMerging(false);
    }
  }, [files, navigate]);

  // Total selected pages across all files
  const totalSelectedPages = files.reduce(
    (sum, f) => sum + f.selectedPages.size,
    0
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-xl font-bold text-gray-900">Merge PDFs</h1>
          <p className="text-sm text-gray-500">
            Combine multiple PDF files into one document
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {/* Drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onClick={handleBrowse}
          className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all mb-6 ${
            isDragging
              ? 'border-brand-blue bg-brand-light/30 scale-[1.01]'
              : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
          }`}
        >
          <Upload
            size={36}
            className={`mx-auto mb-3 ${isDragging ? 'text-brand-blue' : 'text-gray-400'}`}
          />
          <p className="text-lg font-medium text-gray-700">
            Drop PDF files here, or click to browse
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Add multiple files — drag to reorder, then merge
          </p>
        </div>

        <CloudSourceHint multiple onPicked={handleCloudPicked} className="mb-6" />

        {/* Intake errors (locked file) must be visible before any file is
            queued — the controls block below only renders once files exist. */}
        {files.length === 0 && error && (
          <p role="alert" className="text-red-600 text-sm -mt-3 mb-6">{error}</p>
        )}

        {/* File list — uses displayFiles for live drag preview */}
        {files.length > 0 && (
          <div className="space-y-2 mb-6">
            {displayFiles.map((entry, index) => {
              // Find this entry's real index in `files` (for drag source tracking)
              const realIndex = files.indexOf(entry);
              const isBeingDragged = realIndex === dragIndex;
              const isDropTarget =
                dragIndex !== null &&
                dragOverIndex !== null &&
                index === dragOverIndex &&
                dragIndex !== dragOverIndex;

              return (
                <FileCard
                  key={entry.id}
                  entry={entry}
                  index={realIndex}
                  isBeingDragged={isBeingDragged}
                  isDropTarget={isDropTarget}
                  onRemove={removeFile}
                  onToggleExpand={toggleExpand}
                  onTogglePage={togglePage}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOverCard(e, index)}
                />
              );
            })}
          </div>
        )}

        {/* Merge controls */}
        {files.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {error && (
              <p role="alert" className="text-red-600 text-sm mb-4">{error}</p>
            )}

            {merging && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Loader2 size={16} className="animate-spin text-brand-blue" />
                  <span className="text-sm text-gray-600">
                    Merging… {Math.round(progress * 100)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-blue rounded-full transition-all duration-200"
                    style={{ width: `${progress * 100}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {files.length} file{files.length !== 1 ? 's' : ''} ·{' '}
                {totalSelectedPages} page{totalSelectedPages !== 1 ? 's' : ''}{' '}
                selected
              </p>
              <button
                onClick={handleMerge}
                disabled={merging || totalSelectedPages === 0}
                className="flex items-center gap-2 px-6 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Layers size={18} />
                Merge PDFs
              </button>
            </div>
          </div>
        )}

      </div>
      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
  );
}

// --- File card component ---

function FileCard({
  entry,
  index,
  isBeingDragged,
  isDropTarget,
  onRemove,
  onToggleExpand,
  onTogglePage,
  onDragStart,
  onDragEnd,
  onDragOver,
}) {
  const { id, name, size, pdfDoc, numPages, selectedPages, expanded } = entry;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      className={`bg-white rounded-xl border-2 overflow-hidden transition-all duration-200 ${
        isBeingDragged
          ? 'opacity-30 scale-[0.98] border-gray-200'
          : isDropTarget
            ? 'border-brand-blue shadow-md ring-2 ring-brand-blue/20'
            : 'border-gray-200 hover:shadow-sm'
      }`}
    >
      {/* Card header */}
      <div className="flex items-center gap-3 p-4">
        {/* Drag handle */}
        <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
          <GripVertical size={18} />
        </div>

        {/* Thumbnail of first page */}
        <div className="w-10 h-14 rounded border border-gray-200 overflow-hidden shrink-0 bg-white">
          <PageCanvas pdfDoc={pdfDoc} pageNumber={1} scale={0.1} />
        </div>

        {/* File info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
          <p className="text-xs text-gray-500">
            {numPages} page{numPages !== 1 ? 's' : ''} · {formatFileSize(size)}{' '}
            · {selectedPages.size} of {numPages} selected
          </p>
        </div>

        {/* Expand toggle */}
        <button
          onClick={() => onToggleExpand(id)}
          className="p-1.5 rounded-md hover:bg-gray-100 transition-colors text-gray-500"
          title={expanded ? 'Collapse pages' : 'Show pages'}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        {/* Remove */}
        <button
          onClick={() => onRemove(id)}
          className="p-1.5 rounded-md hover:bg-red-50 transition-colors text-gray-400 hover:text-red-500"
          title="Remove file"
        >
          <X size={16} />
        </button>
      </div>

      {/* Expanded page thumbnails */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: numPages }, (_, i) => i + 1).map(
              (pageNum) => (
                <PageThumbnail
                  key={pageNum}
                  pdfDoc={pdfDoc}
                  pageNum={pageNum}
                  selected={selectedPages.has(pageNum)}
                  onToggle={() => onTogglePage(id, pageNum)}
                />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Individual page thumbnail (for expanded view) ---

function PageThumbnail({ pdfDoc, pageNum, selected, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className={`relative rounded-lg overflow-hidden border-2 transition-all ${
        selected
          ? 'border-brand-blue shadow-sm'
          : 'border-gray-300 opacity-40 hover:opacity-70'
      }`}
      title={`Page ${pageNum} — click to ${selected ? 'exclude' : 'include'}`}
    >
      <div className="w-16 h-22">
        <PageCanvas pdfDoc={pdfDoc} pageNumber={pageNum} scale={0.12} />
      </div>
      <span
        className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 text-[10px] px-1.5 py-0.5 rounded-full ${
          selected
            ? 'bg-brand-blue text-white'
            : 'bg-gray-500 text-white'
        }`}
      >
        {pageNum}
      </span>
    </button>
  );
}
